import { devLog } from "./dev-log";

// ============================================================================
// Sign in with Apple JS (web) wrapper
// Parallels performGoogleLogin in social-login.ts: lazy-load the SDK once,
// expose one async function returning a normalized result.
// ============================================================================

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init(config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          nonce?: string;
          usePopup?: boolean;
        }): void;
        signIn(): Promise<{
          authorization: { code: string; id_token: string; state?: string };
          user?: { email?: string; name?: { firstName?: string; lastName?: string } };
        }>;
      };
    };
  }
}

export interface AppleLoginResult {
  code: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const APPLE_SDK_URL = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_SDK_SCRIPT_ID = "apple-signin-jssdk";

/**
 * Feature flag: is Apple web login configured for this build?
 * Empty VITE_APPLE_SERVICES_ID → not configured → callers fall back to Google.
 */
export function isAppleLoginAvailable(): boolean {
  return !!import.meta.env.VITE_APPLE_SERVICES_ID;
}

// Idempotent SDK loader (mirrors FacebookSDKManager.loadSDKScript: singleton
// promise, dedupe by script id, resolve on load / reject on error).
let sdkPromise: Promise<void> | null = null;
function loadAppleSdk(): Promise<void> {
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise<void>((resolve, reject) => {
    if (window.AppleID) {
      resolve();
      return;
    }

    const existing = document.getElementById(APPLE_SDK_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Apple Sign In SDK")));
      return;
    }

    const script = document.createElement("script");
    script.id = APPLE_SDK_SCRIPT_ID;
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Apple Sign In SDK"));
    document.head.appendChild(script);
  });

  return sdkPromise;
}

let initialized = false;
function initAppleAuth(): void {
  if (initialized) {
    return;
  }
  const clientId = import.meta.env.VITE_APPLE_SERVICES_ID;
  if (!clientId) {
    throw new Error("Apple Sign In not configured (VITE_APPLE_SERVICES_ID missing)");
  }
  if (!window.AppleID) {
    throw new Error("Apple Sign In SDK not loaded");
  }
  window.AppleID.auth.init({
    clientId,
    scope: "name email",
    // With usePopup:true the redirect page is a formality, but Apple still
    // validates the return URL/domain registered on the Services ID. Deriving
    // from origin auto-adapts across blidz.com and temporary.blidz.com without
    // an env var per environment.
    redirectURI: `${window.location.origin}/apple-login-return`,
    state: crypto.randomUUID(),
    usePopup: true,
  });
  initialized = true;
}

/**
 * Trigger the Sign in with Apple popup and normalize the result.
 * `user` (email + name) is present ONLY on the very first authorization for an
 * Apple ID; subsequent logins omit it (backend already has the record).
 * User-cancelled / popup-closed rejections should be treated as benign cancels
 * by the caller.
 */
export async function performAppleLogin(): Promise<AppleLoginResult> {
  await loadAppleSdk();
  initAppleAuth();
  const resp = await window.AppleID!.auth.signIn();
  const code = resp?.authorization?.code;
  if (!code) {
    throw new Error("Apple did not return an authorization code");
  }
  devLog("🍎 Apple Sign In succeeded");
  return {
    code,
    email: resp?.user?.email ?? undefined,
    firstName: resp?.user?.name?.firstName ?? undefined,
    lastName: resp?.user?.name?.lastName ?? undefined,
  };
}

export {};
